import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import logOutIcon from "../../../assets/icons/nav_menu_log_out.svg";
import Avatar from "@mui/material/Avatar";
import {
  navbar,
  navbarContainer,
  navbarRow,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useArgonController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import { MenuItem } from "@mui/material";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });
  const navigate = useNavigate();

  // Update user state when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        setUser(userData);
      } catch {
        setUser(null);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Update user state on route changes
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      setUser(userData);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }
    window.addEventListener("scroll", handleTransparentNavbar);

    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token");
    navigate("/user/login");
  };

  const handleOpenProfileMenu = (event) => {
    setOpenProfileMenu(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setOpenProfileMenu(false);
  };

  const menuItem = (icon, title, onClickFn) => (
    <MenuItem onClick={onClickFn}>
      <ListItemIcon>
        <img src={icon} alt="menu_icon" />
      </ListItemIcon>
      <Typography variant="inherit">{title}</Typography>
    </MenuItem>
  );

  const renderProfileMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
    >
      {menuItem(logOutIcon, "Logout", handleLogout)}
    </Menu>
  );

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme, { navbarType })}>
        <ArgonBox></ArgonBox>
        {isMini ? null : (
          <ArgonBox sx={(theme) => navbarRow(theme, { isMini })}>
            <ArgonBox
              color={light ? "white" : "inherit"}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              width="100%"
            >
              {/* Search bar will be rendered here via portal from individual components */}
              <div
                id="navbar-search-portal"
                style={{ flex: 1, marginRight: "16px" }}
              ></div>

              <IconButton size="large" onClick={handleOpenProfileMenu}>
                {!user?.avatar ? (
                  <Icon
                    sx={({ palette: { dark, white } }) => ({
                      color:
                        light && transparentNavbar ? white.main : dark.main,
                      marginRight: 1,
                    })}
                    size="large"
                  >
                    account_circle
                  </Icon>
                ) : (
                  <Avatar
                    src={user?.avatar}
                    alt="profile"
                    variant="rounded"
                    sx={{ mr: 2 }}
                  />
                )}

                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color={light && transparentNavbar ? "white" : "dark"}
                >
                  {user?.name || "Admin"}
                </ArgonTypography>
              </IconButton>

              {renderProfileMenu()}
            </ArgonBox>
          </ArgonBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: true,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
